import {
  type FC,
  type FormEvent,
  type KeyboardEvent,
  useMemo,
  useState,
} from "react";

import { Text } from "../common/Text";

interface EmailOtpCardProps {
  onRequestOtp: (email: string) => Promise<void>;
  onVerifyOtp: (
    email: string,
    code: string,
    rememberDevice: boolean,
  ) => Promise<void>;
  title?: string;
  subtitle?: string;
  className?: string;
  surface?: "landing" | "standalone";
}

const SUCCESS_ICON_PATH =
  "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z";
const ERROR_ICON_PATH =
  "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z";

const NoticeBanner: FC<{
  iconPath: string;
  children: string;
  tone: "error" | "success";
}> = ({ iconPath, children, tone }) => {
  const toneClassName =
    tone === "success"
      ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
      : "border-rose-500/30 bg-rose-500/10 text-rose-400";

  return (
    <div
      className={`flex items-start gap-2.5 rounded-none border px-3.5 py-3 font-mono text-sm ${toneClassName}`}
    >
      <svg
        className="mt-0.5 h-4 w-4 flex-shrink-0"
        fill="currentColor"
        viewBox="0 0 20 20"
        aria-hidden="true"
      >
        <path fillRule="evenodd" d={iconPath} clipRule="evenodd" />
      </svg>
      {children}
    </div>
  );
};

export const EmailOtpCard: FC<EmailOtpCardProps> = ({
  onRequestOtp,
  onVerifyOtp,
  title = "Create your workspace",
  subtitle = "Use your work email to get a passwordless code and access the dashboard.",
  className = "",
  surface = "landing",
}) => {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [requested, setRequested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rememberDevice, setRememberDevice] = useState(false);

  const canSubmitEmail = useMemo(() => email.trim().length > 3, [email]);
  const canSubmitCode = useMemo(
    () => requested && code.trim().length > 3,
    [requested, code],
  );

  const shellClassName =
    surface === "landing"
      ? "border-2 border-slate-700 bg-arti-panel p-6"
      : "border-2 border-slate-700 bg-arti-panel p-6";

  const fieldClassName =
    "mt-2 w-full rounded-none border-2 border-slate-700 bg-arti-background px-4 py-3 font-mono text-sm text-slate-100 outline-none transition focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 placeholder:text-slate-600";

  const primaryButtonClassName =
    "inline-flex w-full cursor-pointer items-center justify-center rounded-none bg-emerald-500 px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-950 transition hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 disabled:cursor-not-allowed disabled:opacity-60";

  const secondaryButtonClassName =
    "inline-flex w-full cursor-pointer items-center justify-center rounded-none border-2 border-slate-700 bg-transparent px-5 py-3 font-mono text-[11px] font-bold uppercase tracking-widest text-slate-400 transition hover:border-emerald-500/50 hover:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/30";

  let submitLabel = "Get access";
  if (loading) {
    submitLabel = requested ? "Verifying..." : "Sending...";
  } else if (requested) {
    submitLabel = "Verify code";
  }

  const handleRequest = async () => {
    if (!canSubmitEmail) {
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      await onRequestOtp(email.trim());
      setRequested(true);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : "Unable to send code.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!canSubmitCode) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await onVerifyOtp(email.trim(), code.trim(), rememberDevice);
    } catch (verificationError) {
      setError(
        verificationError instanceof Error
          ? verificationError.message
          : "Unable to verify code.",
      );
      setLoading(false);
      return;
    }

    setLoading(false);
  };

  const submitCurrentStep = async () => {
    if (loading) {
      return;
    }

    await (requested ? handleVerify() : handleRequest());
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    void submitCurrentStep();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== "Enter") {
      return;
    }
    if (event.shiftKey || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }
    event.preventDefault();
    void submitCurrentStep();
  };

  return (
    <div className={`${shellClassName} ${className}`.trim()}>
      <div className="mb-6 space-y-3">
        <div className="inline-flex items-center rounded-none border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-emerald-400">
          <span className="mr-1.5 text-[8px]">■</span>
          Passwordless access
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold tracking-tight text-white">
            {requested ? "Verify your email" : title}
          </h2>
          <p className="text-sm leading-6 text-slate-400">
            {requested ? (
              <>
                We sent a six-digit code to{" "}
                <span className="font-medium text-slate-200">
                  {email.trim()}
                </span>
                . Enter it below to continue.
              </>
            ) : (
              subtitle
            )}
          </p>
        </div>
      </div>

      <form
        className="space-y-4"
        onSubmit={handleSubmit}
        onKeyDown={handleKeyDown}
      >
        {requested ? null : (
          <label className="block">
            <Text variant="label">Work email</Text>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="you@company.com"
              required
              autoComplete="email"
              className={fieldClassName}
            />
          </label>
        )}

        {requested ? (
          <>
            <label className="block">
              <Text variant="label">One-time code</Text>
              <input
                type="text"
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="123456"
                required
                autoFocus
                autoComplete="one-time-code"
                className={`${fieldClassName} text-center text-lg tracking-[0.38em]`}
              />
            </label>

            <label className="flex cursor-pointer items-center gap-3 rounded-none border border-slate-700 bg-arti-background px-4 py-3 font-mono text-sm text-slate-400">
              <input
                type="checkbox"
                checked={rememberDevice}
                onChange={(event) => setRememberDevice(event.target.checked)}
                className="h-4 w-4 rounded-none border-slate-700 bg-arti-background text-emerald-500 focus:ring-emerald-500/30"
              />
              <span>Keep me signed in on this device</span>
            </label>
          </>
        ) : null}

        {message ? (
          <NoticeBanner iconPath={SUCCESS_ICON_PATH} tone="success">
            {message}
          </NoticeBanner>
        ) : null}

        {error ? (
          <NoticeBanner iconPath={ERROR_ICON_PATH} tone="error">
            {error}
          </NoticeBanner>
        ) : null}

        <button
          type="submit"
          disabled={requested ? !canSubmitCode : !canSubmitEmail}
          className={primaryButtonClassName}
        >
          {submitLabel}
        </button>

        {requested ? (
          <button
            type="button"
            className={secondaryButtonClassName}
            onClick={() => {
              setRequested(false);
              setCode("");
              setMessage(null);
              setError(null);
            }}
          >
            Use a different email
          </button>
        ) : null}
      </form>

      <p className="mt-5 text-center font-mono text-[10px] uppercase leading-5 tracking-widest text-slate-500">
        Approved teams only
      </p>
    </div>
  );
};
